import { useState, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message, Tooltip } from 'antd';
import {
    FileTextOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    PaperClipOutlined,
} from '@ant-design/icons';
import StatusBadge from '@/components/ui/StatusBadge';
import UserAvatar from '@/components/ui/UserAvatar';
import {
    getProjectSubmissionMemos,
    createProjectSubmissionMemo,
    updateProjectSubmissionMemo,
    deleteProjectSubmissionMemo,
} from '@services/projects.service';
import { getProjectTasks } from '@services/task/task.service';
import { formatDate, getInitials, getAvatarColor, truncateFileName } from '@hooks/projects/projectHelpers';
import { resolveProfileImageUrl } from '@hooks/projects/detail/useProjectDetail';
import { htmlToMarkdown } from '@utils/htmlToMarkdown.util';
import { contentToApiMentions } from '@utils/richTextMentions';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_SUBMISSION_MEMOS_CREATE,
    PROJECT_SUBMISSION_MEMOS_UPDATE,
    PROJECT_SUBMISSION_MEMOS_DELETE,
} from '@/ability';
import { ENV } from '@configs/env';

export const SUBMISSION_MEMOS_QUERY_KEY = (projectId) => ['projects', projectId, 'submission-memos'];

/**
 * Hook that owns all data, mutations, KPI values, table columns, detail/edit modal,
 * and attachment state for the Submission Memos tab.
 */
export const useProjectSubmissionMemos = (projectId, project) => {
    const queryClient = useQueryClient();
    const can = useCanCheck();

    // ── Permissions ──
    const canCreate = can(PROJECT_SUBMISSION_MEMOS_CREATE);
    const canUpdate = can(PROJECT_SUBMISSION_MEMOS_UPDATE);
    const canDelete = can(PROJECT_SUBMISSION_MEMOS_DELETE);

    // ── Create modal state ──
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [form] = Form.useForm();

    // ── Detail / edit modal state ──
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedMemo, setSelectedMemo] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm] = Form.useForm();

    // ── Editor attachment state (shared for create) ──
    const [pendingFiles, setPendingFiles] = useState([]);
    const [pendingLinks, setPendingLinks] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkForm] = Form.useForm();
    const linkCallbackRef = useRef(null);

    // ── Edit attachment state ──
    const [editPendingFiles, setEditPendingFiles] = useState([]);
    const [editPendingLinks, setEditPendingLinks] = useState([]);
    const [editLinkModalOpen, setEditLinkModalOpen] = useState(false);
    const [editLinkForm] = Form.useForm();
    const editLinkCallbackRef = useRef(null);

    // ── Queries ──
    const {
        data: memoPayload,
        isLoading: listLoading,
        isRefetching: listRefetching,
        refetch,
    } = useQuery({
        queryKey: SUBMISSION_MEMOS_QUERY_KEY(projectId),
        queryFn: () => getProjectSubmissionMemos(projectId, { page: 1, limit: 100 }),
        enabled: !!projectId,
    });

    const memos = useMemo(() => memoPayload?.data ?? [], [memoPayload]);

    const { data: tasks = [], isLoading: tasksLoading } = useQuery({
        queryKey: ['projects', projectId, 'tasks-list'],
        queryFn: () => getProjectTasks(projectId),
        enabled: !!projectId && (createModalOpen || isEditing),
    });

    const taskOptions = useMemo(() => {
        const baseTasks = [...tasks];
        // Merge missing tasks from the selected memo so they show up correctly in the Select dropdown
        if (selectedMemo?.tasks) {
            selectedMemo.tasks.forEach((mt) => {
                const id = mt.taskId ?? mt.id ?? mt.TaskId ?? mt._id;
                if (!baseTasks.some((t) => (t.id ?? t.TaskId ?? t._id) === id)) {
                    baseTasks.push(mt);
                }
            });
        }
        return baseTasks.map((t) => {
            const taskId = t.id ?? t.taskId ?? t.TaskId ?? t._id;
            const keyLabel = t.title || t.name || 'Untitled Task';
            const statusLabel = t.status ? ` [${t.status}]` : '';
            return { value: taskId, label: `${keyLabel}${statusLabel}` };
        });
    }, [tasks, selectedMemo]);

    // ── Create mutation ──
    const createMutation = useMutation({
        mutationFn: (body) => createProjectSubmissionMemo(projectId, body),
        onSuccess: () => {
            message.success('Submission memo created successfully');
            queryClient.invalidateQueries({ queryKey: SUBMISSION_MEMOS_QUERY_KEY(projectId) });
            closeCreateModal();
        },
        onError: (err) => {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to create submission memo';
            message.error(msg);
        },
    });

    // ── Update mutation ──
    const updateMutation = useMutation({
        mutationFn: ({ memoId, body }) => updateProjectSubmissionMemo(projectId, memoId, body),
        onSuccess: () => {
            message.success('Submission memo updated');
            queryClient.invalidateQueries({ queryKey: SUBMISSION_MEMOS_QUERY_KEY(projectId) });
            closeDetailModal();
        },
        onError: (err) => {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to update';
            message.error(msg);
        },
    });

    // ── Delete mutation ──
    const deleteMutation = useMutation({
        mutationFn: (memoId) => deleteProjectSubmissionMemo(projectId, memoId),
        onSuccess: () => {
            message.success('Submission memo deleted');
            queryClient.invalidateQueries({ queryKey: SUBMISSION_MEMOS_QUERY_KEY(projectId) });
            closeDetailModal();
        },
        onError: (err) => {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to delete';
            message.error(msg);
        },
    });

    // ── Create modal actions ──
    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        form.resetFields();
        setPendingFiles([]);
        setPendingLinks([]);
    }, [form]);

    // ── Detail modal actions ──
    const openDetailModal = useCallback((memo) => {
        setSelectedMemo(memo);
        setIsEditing(false);
        setDetailModalOpen(true);
    }, []);

    const closeDetailModal = useCallback(() => {
        setDetailModalOpen(false);
        setSelectedMemo(null);
        setIsEditing(false);
        editForm.resetFields();
        setEditPendingFiles([]);
        setEditPendingLinks([]);
    }, [editForm]);

    const startEditing = useCallback(() => {
        if (!selectedMemo) return;
        editForm.setFieldsValue({
            taskIds: (selectedMemo.tasks ?? []).map((t) => t.taskId ?? t.id ?? t.TaskId ?? t._id),
            notes: selectedMemo.notes?.markdown ?? selectedMemo.notes ?? '',
        });
        setEditPendingFiles([]);
        setEditPendingLinks([]);
        setIsEditing(true);
    }, [selectedMemo, editForm]);

    const handleDelete = useCallback(() => {
        if (!selectedMemo) return;
        const memoId = selectedMemo.id ?? selectedMemo._id;
        deleteMutation.mutate(memoId);
    }, [selectedMemo, deleteMutation]);

    // ── Create attachment handlers ──
    const handleFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `file-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const removeFileAttachment = useCallback((id) => {
        setPendingFiles((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkCallbackRef.current = insertLink;
            linkForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkForm],
    );

    const handleLinkSubmit = useCallback(() => {
        linkForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) { message.warning('Enter URL'); return; }
            const insertLink = linkCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setPendingLinks((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setLinkModalOpen(false);
            linkCallbackRef.current = null;
        });
    }, [linkForm]);

    const removeLinkAttachment = useCallback((id) => {
        setPendingLinks((prev) => prev.filter((l) => l.id !== id));
    }, []);

    // ── Edit attachment handlers ──
    const handleEditFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `edit-file-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setEditPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const removeEditFileAttachment = useCallback((id) => {
        setEditPendingFiles((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const handleEditLinkRequest = useCallback(
        (insertLink) => {
            editLinkCallbackRef.current = insertLink;
            editLinkForm.resetFields();
            setEditLinkModalOpen(true);
        },
        [editLinkForm],
    );

    const handleEditLinkSubmit = useCallback(() => {
        editLinkForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) { message.warning('Enter URL'); return; }
            const insertLink = editLinkCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setEditPendingLinks((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setEditLinkModalOpen(false);
            editLinkCallbackRef.current = null;
        });
    }, [editLinkForm]);

    const removeEditLinkAttachment = useCallback((id) => {
        setEditPendingLinks((prev) => prev.filter((l) => l.id !== id));
    }, []);

    // ── Build FormData / JSON body helper ──
    const buildMemoBody = useCallback((values, files, links) => {
        const notesHtml = contentToApiMentions(values.notes ?? '');
        const notesMarkdown = htmlToMarkdown(notesHtml);
        const taskIds = values.taskIds || [];
        const linkAttachments = links.map(({ url, title }) => ({ type: 'link', url, title }));

        if (files.length > 0) {
            const formData = new FormData();
            if (taskIds.length > 0) formData.append('taskIds', JSON.stringify(taskIds));
            formData.append('notes', JSON.stringify({ markdown: notesMarkdown }));
            if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
            files.forEach((f) => formData.append('files', f.file));
            return formData;
        }
        return { taskIds, notes: { markdown: notesMarkdown }, attachments: linkAttachments };
    }, []);

    // ── Create form submit ──
    const handleSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            createMutation.mutate(buildMemoBody(values, pendingFiles, pendingLinks));
        });
    }, [form, pendingFiles, pendingLinks, createMutation, buildMemoBody]);

    // ── Edit form submit ──
    const handleEditSubmit = useCallback(() => {
        editForm.validateFields().then((values) => {
            const memoId = selectedMemo?.id ?? selectedMemo?._id;
            if (!memoId) return;
            updateMutation.mutate({ memoId, body: buildMemoBody(values, editPendingFiles, editPendingLinks) });
        });
    }, [editForm, selectedMemo, editPendingFiles, editPendingLinks, updateMutation, buildMemoBody]);

    const mentionUsers = useMemo(
        () => (project?.assignedUsers ?? []).slice(0, 50),
        [project?.assignedUsers],
    );

    // ── KPI data ──
    const totalMemos = memoPayload?.total ?? memos.length;
    const submittedCount = useMemo(
        () => memos.filter((m) => (m.status || '').toLowerCase() === 'submitted').length,
        [memos],
    );
    const totalLinkedTasks = useMemo(
        () => memos.reduce((sum, m) => sum + (m.tasks?.length ?? 0), 0),
        [memos],
    );
    const totalAttachments = useMemo(
        () => memos.reduce((sum, m) => sum + (m.attachments?.length ?? 0), 0),
        [memos],
    );

    // ── Table data ──
    const tableData = useMemo(
        () =>
            memos.map((m) => {
                const submitter = m.submittedBy ?? m.creator ?? m.createdBy ?? m.creatorUser ?? {};
                return {
                    key: m.id ?? m._id,
                    id: m.id ?? m._id,
                    submitter,
                    submitterName: submitter.name ?? submitter.fullName ?? 'Unknown',
                    tasksCount: m.tasks?.length ?? 0,
                    taskTitles: (m.tasks ?? []).map((t) => t.title || 'Untitled Task').join(', '),
                    status: m.status ?? 'submitted',
                    attachmentsCount: m.attachments?.length ?? 0,
                    submittedAt: m.submittedAt ?? m.createdAt,
                    notes: m.notes?.markdown ?? m.notes ?? '',
                    _raw: m,
                };
            }),
        [memos],
    );

    const columns = useMemo(
        () => [
            {
                title: 'Date',
                key: 'date',
                width: '160px',
                render: (_, record) => (record.submittedAt ? formatDate(record.submittedAt) : '—'),
            },
            {
                title: 'Notes',
                key: 'notes',
                width: '280px',
                render: (_, record) => {
                    const text = record.notes || '';
                    const truncated = text.length > 80 ? text.slice(0, 80) + '…' : text;
                    return (
                        <Tooltip title={text.length > 80 ? text : undefined}>
                            <span style={{ fontSize: 13, opacity: text ? 1 : 0.4 }}>
                                {truncated || '—'}
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Linked Tasks',
                key: 'tasks',
                width: '140px',
                render: (_, record) =>
                    record.tasksCount > 0 ? `${record.tasksCount} task${record.tasksCount > 1 ? 's' : ''}` : '—',
            },
            {
                title: 'Attachments',
                key: 'attachments',
                width: '110px',
                render: (_, record) => (record.attachmentsCount > 0 ? record.attachmentsCount : '—'),
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: '130px',
                render: (val) => <StatusBadge status={val} />,
            },
            {
                title: 'Submitted By',
                key: 'submittedBy',
                width: '180px',
                render: (_, record) => {
                    const u = record.submitter ?? {};
                    const name = u.name ?? u.fullName ?? 'Unknown';
                    const profileImg = u.profileImage
                        ? resolveProfileImageUrl(u.profileImage, ENV.apiUrl)
                        : undefined;
                    return (
                        <UserAvatar
                            name={name}
                            imageUrl={profileImg}
                            initials={getInitials(name)}
                            avatarBg={getAvatarColor(u.id ?? name)}
                            size={50}
                        />
                    );
                },
            },
        ],
        [],
    );

    const refetchMemos = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: SUBMISSION_MEMOS_QUERY_KEY(projectId) });
    }, [queryClient, projectId]);

    return {
        // List / loading
        memos,
        listLoading,
        listRefetching,
        refetch,
        refetchMemos,

        // KPIs
        totalMemos,
        submittedCount,
        totalLinkedTasks,
        totalAttachments,

        // Table
        tableData,
        columns,

        // Permissions
        canCreate,
        canUpdate,
        canDelete,

        // Create modal
        createModalOpen,
        setCreateModalOpen,
        closeCreateModal,
        createMutation,

        // Detail / edit modal
        detailModalOpen,
        selectedMemo,
        isEditing,
        openDetailModal,
        closeDetailModal,
        startEditing,
        handleDelete,
        updateMutation,
        deleteMutation,
        editForm,
        handleEditSubmit,

        // Form
        form,
        taskOptions,
        tasksLoading,
        mentionUsers,
        handleSubmit,

        // Create attachments
        pendingFiles,
        pendingLinks,
        handleFilesSelected,
        removeFileAttachment,
        handleLinkRequest,
        handleLinkSubmit,
        removeLinkAttachment,

        // Edit attachments
        editPendingFiles,
        editPendingLinks,
        handleEditFilesSelected,
        removeEditFileAttachment,
        handleEditLinkRequest,
        handleEditLinkSubmit,
        removeEditLinkAttachment,

        // Create link modal
        linkModalOpen,
        setLinkModalOpen,
        linkForm,
        linkCallbackRef,

        // Edit link modal
        editLinkModalOpen,
        setEditLinkModalOpen,
        editLinkForm,
        editLinkCallbackRef,
    };
};
